import React, { useEffect, useRef } from 'react';
import Chart from 'chart.js/auto';

const PortfolioGraph = ({ returnsData, isLoading, error, textColor }) => {
    const chartRef = useRef(null);
    const chartInstance = useRef(null);

    useEffect(() => {
        if (chartInstance.current) {
            chartInstance.current.destroy();
        }

        if (!returnsData || isLoading) return;

        const ctx = chartRef.current.getContext('2d');
        const processedData = processReturnsData(returnsData);

        const gradient = ctx.createLinearGradient(0, 0, 0, 180);
        gradient.addColorStop(0, 'rgba(59, 130, 246, 0.15)');
        gradient.addColorStop(1, 'rgba(59, 130, 246, 0.01)');

        chartInstance.current = new Chart(ctx, {
            type: 'line',
            data: {
                labels: processedData.labels,
                datasets: [{
                    label: 'Returns',
                    data: processedData.data,
                    borderColor: '#3b82f6',
                    backgroundColor: gradient,
                    tension: 0.4,
                    fill: true,
                    pointRadius: 2,
                    borderWidth: 1.5
                }]
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                    legend: { display: false },
                    tooltip: {
                        backgroundColor: 'rgba(255, 255, 255, 0.95)',
                        titleColor: '#333',
                        bodyColor: '#333',
                        borderColor: '#f1f5f9',
                        borderWidth: 1,
                        displayColors: false,
                        callbacks: {
                            label: (context) => `${context.parsed.y > 0 ? '+' : ''}${context.parsed.y.toFixed(2)}%`
                        }
                    }
                },
                scales: {
                    x: {
                        grid: { display: false },
                        ticks: {
                            color: textColor ? `${textColor}80` : '#9ca3af',
                            font: { size: 8 },
                            maxRotation: 0
                        },
                        border: { display: false }
                    },
                    y: {
                        grid: { color: 'rgba(0, 0, 0, 0.03)' },
                        ticks: {
                            callback: (value) => value > 0 ? `+${value}%` : `${value}%`,
                            color: textColor ? `${textColor}80` : '#9ca3af',
                            font: { size: 8 },
                            maxTicksLimit: 4
                        },
                        border: { display: false }
                    }
                }
            }
        });

        return () => {
            if (chartInstance.current) {
                chartInstance.current.destroy();
            }
        };
    }, [returnsData, isLoading, textColor]);

    const processReturnsData = (data) => {
        if (!data) return { labels: [], data: [] };

        const labels = [];
        const chartData = [];

        if (Array.isArray(data) && data.length > 0 && 'year' in data[0] && 'quarter' in data[0]) {
            const sortedData = [...data].sort((a, b) => {
                if (a.year !== b.year) return a.year - b.year;
                return a.quarter.localeCompare(b.quarter);
            });

            sortedData.forEach(item => {
                labels.push(`${item.quarter} ${item.year}`);
                chartData.push(parseFloat(item.rate_of_return));
            });
        } else if (typeof data === 'object' && !Array.isArray(data)) {
            const customerId = Object.keys(data)[0];
            if (customerId && data[customerId]) {
                const yearData = data[customerId];

                Object.keys(yearData).sort().forEach(year => {
                    const quarterlyData = yearData[year];
                    quarterlyData.forEach((value, index) => {
                        labels.push(`Q${index + 1} ${year}`);
                        chartData.push(parseFloat(value));
                    });
                });
            }
        }

        return { labels, data: chartData };
    };

    const calculateAverageReturn = () => {
        if (!returnsData) return null;

        let values = [];

        if (Array.isArray(returnsData) && returnsData.length > 0 && 'rate_of_return' in returnsData[0]) {
            values = returnsData.map(item => parseFloat(item.rate_of_return));
        } else if (typeof returnsData === 'object' && !Array.isArray(returnsData)) {
            const customerId = Object.keys(returnsData)[0];
            if (customerId && returnsData[customerId]) {
                Object.values(returnsData[customerId]).forEach(quarterlyData => {
                    values = values.concat(quarterlyData.map(val => parseFloat(val)));
                });
            }
        }

        if (values.length === 0) return null;

        const sum = values.reduce((acc, val) => acc + val, 0);
        return (sum / values.length).toFixed(2);
    };

    const averageReturn = calculateAverageReturn();
    const isPositiveReturn = averageReturn && parseFloat(averageReturn) > 0;

    return (
        <div className="flex-1 min-w-[250px] bg-white p-3 rounded-lg shadow-sm border border-gray-100">
            <div className="flex justify-between items-center mb-2">
                <h3 className="text-base font-medium" style={{ color: textColor || 'inherit' }}>
                    Performance
                </h3>

                {averageReturn && (
                    <div className={`text-xs font-medium px-1.5 py-0.5 rounded-full ${isPositiveReturn ? 'bg-green-50 text-green-600' : 'bg-red-50 text-red-600'
                        }`}>
                        {isPositiveReturn ? '+' : ''}{averageReturn}% avg
                    </div>
                )}
            </div>

            <div className="h-[150px] relative">
                {isLoading ? (
                    <div className="absolute inset-0 flex items-center justify-center bg-gray-50 rounded">
                        <div className="text-xs text-gray-500 flex items-center">
                            <svg className="animate-spin -ml-1 mr-2 h-3 w-3 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            Loading...
                        </div>
                    </div>
                ) : error ? (
                    <div className="absolute inset-0 flex items-center justify-center bg-gray-50 rounded">
                        <div className="text-xs text-red-500">Data unavailable</div>
                    </div>
                ) : !returnsData || (Array.isArray(returnsData) && returnsData.length === 0) ? (
                    <div className="absolute inset-0 flex items-center justify-center bg-gray-50 rounded">
                        <div className="text-xs text-gray-500">No data available</div> </div>
                ) : (
                    <canvas ref={chartRef}></canvas>
                )}
            </div>
        </div>);
};
export default PortfolioGraph;